import { useEffect, useState, useMemo, useCallback } from 'react'
import moment from 'moment-timezone'
import { getReport } from 'utils/api'
import reduce from 'lodash/reduce'

const TIMEZONE = moment.tz.guess()
const DATES = ['edited', 'purchased', 'closed']
const DEFAULT_FILTERS = {
  statusLive: true,
  statusClosed: false,
  statusPending: false,
  statusPaused: false,
  launchStart: '',
  launchEnd: '',
  closedStart: '',
  closedEnd: '',
  editedStart: '',
  editedEnd: ''
}

/**
 * Return array representation of some csv data
 * @param {String} data
 */
const csvToArray = data =>
  data
    .trim()
    .split('\r\n')
    .map(line => JSON.parse(`[${line}]`))

const formatFiltersForAPI = filters =>
  reduce(
    filters,
    (accumulator, value, key) => {
      if (key.startsWith('status')) {
        return value
          ? {
              ...accumulator,
              status: [
                ...(accumulator.status || []),
                key.replace('status', '').toLowerCase()
              ],
            }
          : accumulator
      }
      return {
        [key]: value,
        ...accumulator
      }
    },
    {}
  )

/**
 * Fetches report asynchronously from API based on filters
 * @param {Object} filters
 */
export const useFetchReport = (filters, isSaved) => {
  const [isPending, setIsPending] = useState(false)
  const [reportAPIError, setReportAPIError] = useState(null)
  const [reportData, setReportData] = useState([])

  const setLoadingState = () => {
    setIsPending(true)
    setReportAPIError(null)
    setReportData([])
  }

  const makeResponseError = async (response, apiParameters) => {
    const responseJson = JSON.stringify(await response.json(), null, 4)
    const parametersJson = JSON.stringify(apiParameters)
    const error = new Error(`${responseJson}\nFilters: ${parametersJson}`)
    error.response = response
    return error
  }

  useEffect(() => {
    const doGetReport = async () => {
      try {
        const apiParameters = formatFiltersForAPI(filters)
        const response = await getReport(apiParameters)
        if (!response.ok) {
          throw await makeResponseError(response, apiParameters)
        }
        const reader = response.body.getReader()
        const decoder = new TextDecoder()

        while (true) {
          const { value, done } = await reader.read()
          const csvData = decoder.decode(value)

          if (csvData && csvData.length) {
            const data = csvToArray(csvData)
            setReportData(prevData => [...prevData, ...data])
          }

          if (done) {
            setIsPending(false)
            return
          }
        }
      } catch (e) {
        setReportAPIError(e)
      }
    }

    if (isSaved) {
      setLoadingState()
      doGetReport()
    }
  }, [filters, isSaved])

  return {
    isPending,
    reportProgress: reportData && Math.max(0, reportData.length - 1),
    reportData,
    reportAPIError
  }
}

/**
 * Transforms report response data to what useTable expects
 * @param {Array} data
 */
export const useTabulizeReportData = (data = []) => {
  const [headers, ...rows] = data
  const tableColumns = useMemo(
    () =>
      headers.map((header, idx) => ({
        Header: header,
        accessor: String(idx)
      })),
    [data]
  )
  const tableData = useMemo(
    () =>
      rows.map(row =>
        row.map((datum, idx) => {
          // Format timestamps
          if (datum && DATES.includes(headers[idx].toLowerCase())) {
            return moment
              .unix(datum)
              .tz(TIMEZONE)
              .format('YYYY-MM-DD HH:MM z')
          }

          return datum
        })
      ),
    [data]
  )

  return {
    tableColumns,
    tableData
  }
}

/**
 * Manages report filters state and stores it in localStorage
 */
export const useReportFilters = () => {
  let initialFilters = DEFAULT_FILTERS
  try {
    initialFilters = JSON.parse(localStorage.getItem('reportFilters') || '')
  } catch (e) {
    // Just ignore storage if empty or invalid
  }

  const [isSaved, setIsSaved] = useState(false)
  const [filters, setFilters] = useState(initialFilters)
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
    localStorage.setItem('reportFilters', '')
  }, [])
  const saveFilters = useCallback(newFilters => {
    setFilters(newFilters)
    localStorage.setItem('reportFilters', JSON.stringify(newFilters))
    setIsSaved(true)
  }, [])

  return { isSaved, filters, saveFilters, resetFilters }
}



// WEBPACK FOOTER //
// ./src/routes/admin/hooks.js